import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin, signOutOfGoogle } from "@/pages/AuthCallback";
import { toast } from "sonner";
import GoogleIcon from "@/components/GoogleIcon";
import { formatApiErrorDetail, track, applyPreAuthData } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";
import RotatingHero from "@/components/RotatingHero";
import SignatureSoundControl from "@/components/SignatureSoundControl";
import { useSeo } from "@/lib/seo";

const SIDE =
  "https://images.unsplash.com/photo-1600144559281-53d1dee79a99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxOTB8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBibGFjayUyMHdvbWFuJTIwcG9ydHJhaXQlMjB3YXJtJTIwYW1iaWVudCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4NjgxMDczNHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Sign In to Sisterhood+ | Women's Emotional Wellness App",
    description: "Sign in to your Sisterhood+ account to continue your journaling, daily check-ins, and growth.",
    path: "/login",
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      const updated = await applyPreAuthData(u);
      if (updated) setUser(updated);
      track("login_success");
      navigate(updated?.onboarding_completed ? "/home" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FCFAF6]">
      <SignatureSoundControl />
      <div className="relative hidden lg:block">
        <RotatingHero />
        <div className="absolute inset-0 bg-gradient-to-t from-[#291B2D] via-[#291B2D]/40 to-transparent" />
        <div className="absolute bottom-14 left-12 right-12">
          <p className="font-display text-3xl text-[#FCFAF6] leading-snug">Welcome back to your space.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-md">
          <Link to="/" data-testid="login-logo-home" className="inline-flex items-center gap-2.5 group">
            <span className="w-10 h-10 rounded-2xl bg-[#291B2D] text-white flex items-center justify-center font-heading text-lg group-hover:scale-105 transition-smooth">S<span className="text-[#C89B55]">+</span></span>
            <span className="font-heading text-2xl">Sisterhood<span className="text-[#C89B55]">+</span></span>
          </Link>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-8">Sign in</h1>
          <p className="text-[#59535B] mt-2">Continue where you left off.</p>

          {error && (
            <div data-testid={LOGIN.error} className="mt-6 bg-[#FAF0F0] border border-[#9E3A3A]/30 text-[#9E3A3A] rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#252326]">Email</label>
              <input data-testid={LOGIN.emailInput} type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-[#FCFAF6] border border-[#E5DED4] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C89B55] transition-all" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#252326]">Password</label>
              <input data-testid={LOGIN.passwordInput} type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-[#FCFAF6] border border-[#E5DED4] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C89B55] transition-all" />
            </div>
            <button data-testid={LOGIN.submitButton} type="submit" disabled={loading}
              className="w-full rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] active:scale-[0.99] transition-all px-7 py-3.5 font-medium disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <Link data-testid="login-forgot-link" to="/forgot-password"
            className="mt-3 inline-block text-sm text-[#8A838E] hover:text-[#291B2D] underline underline-offset-4">
            Forgot your password?
          </Link>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-[#E5DED4] flex-1" />
            <span className="text-xs text-[#8A838E] uppercase tracking-wider">or</span>
            <div className="h-px bg-[#E5DED4] flex-1" />
          </div>

          <button data-testid={LOGIN.googleButton} onClick={() => { track("social_auth_clicked", { provider: "google" }); startGoogleLogin(); }}
            className="w-full rounded-full bg-[#F7F2EA] border border-[#E5DED4] hover:bg-[#EFE7DC] transition-all px-6 py-3.5 font-medium flex items-center justify-center gap-2.5">
            <GoogleIcon size={18} /> Continue with Google
          </button>

          <button data-testid="login-google-switch" onClick={() => { signOutOfGoogle(); toast.info("We opened Google in a new tab — sign out of (or switch away from) the account you don't want, then come back and tap “Continue with Google”."); }}
            className="w-full mt-2.5 text-sm text-[#8A838E] hover:text-[#291B2D] underline underline-offset-4 text-center">
            Use a different Google account
          </button>

          <p className="mt-8 text-center text-[#59535B]">
            New here?{" "}
            <Link data-testid={LOGIN.registerLink} to="/register" className="text-[#291B2D] font-semibold underline underline-offset-4">
              Create an account
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
